import { Col, Row, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../../shared/permissions';
import { type DealerWithPermissionsFragmentFragment } from '../../../../api/fragments/DealerWithPermissionsFragment';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import useTranslatedString from '../../../../utilities/useTranslatedString';
import MarketTypeField from '../../../shared/MarketTypeField';
import DealerContact from './DealerContact';
import DealerDisclaimers from './DealerDisclaimers';
import DealerIntegrationDetails from './DealerIntegrationDetails';

const colSpan = { lg: 8, md: 12, xs: 24 };

export type DealerFormProps = {
    dealer: DealerWithPermissionsFragmentFragment;
};

const DealerForm = ({ dealer }: DealerFormProps) => {
    const { t } = useTranslation('dealerForm');
    const { yesNoSwitch } = useSystemSwitchData();
    const { company } = dealer;
    const translatedString = useTranslatedString();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: dealer.versioning,
        timeZone: dealer.company.timeZone,
    });

    const hasUpdatePermission = hasPermissions(dealer.permissions, [permissionKind.updateDealer]);

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('dealerForm:mainDetailsTab')}>
                <Panel key={t('dealerForm:mainDetailsTab')} header={t('dealerForm:mainDetailsTab')}>
                    <Row gutter={14}>
                        <Col {...colSpan}>
                            <FormFields.InputField
                                {...t('dealerForm:fields.dealerInput.displayName', { returnObjects: true })}
                                disabled={!hasUpdatePermission}
                                name="dealerInput.displayName"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.TranslatedInputField
                                {...t('dealerForm:fields.dealerInput.legalName', { returnObjects: true })}
                                disabled={!hasUpdatePermission}
                                name="dealerInput.legalName"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.DisplayField
                                {...t('dealerForm:fields.company', { returnObjects: true })}
                                disabled={!hasUpdatePermission}
                                value={translatedString(dealer.company.legalName)}
                            />
                        </Col>
                        <MarketTypeField
                            companyId={dealer.company?.id}
                            countryCode={dealer.company?.countryCode}
                            disabled={!hasUpdatePermission}
                            prefix="dealerInput"
                        />
                        <Col {...colSpan}>
                            <FormFields.SwitchField
                                {...t('dealerForm:fields.dealerInput.isActive', { returnObjects: true })}
                                disabled={!hasUpdatePermission}
                                name="dealerInput.isActive"
                                {...yesNoSwitch}
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.DisplayFieldWithUTCOffset
                                {...t('common:fields.updated', { returnObjects: true, offset })}
                                value={updated}
                            />
                        </Col>
                    </Row>
                </Panel>
            </CollapsibleWrapper>

            {(company.availableModules.hasFinanceProducts || company.availableModules.hasInsuranceModules) && (
                <DealerDisclaimers dealer={dealer} disabled={!hasUpdatePermission} />
            )}
            <DealerContact disabled={!hasUpdatePermission} />
            <DealerIntegrationDetails
                disabled={!hasPermissions(dealer.permissions, [permissionKind.updateDealerIntegration])}
                hasAutoplay={dealer?.company?.availableModules.hasAutoplayModules}
            />
        </Space>
    );
};

export default DealerForm;
